/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/04/2022
 */

import * as React from 'react';
import Page from '@/wpt-lib/pdf/common/Page';
import { Cell, Row, Table } from '@/wpt-lib/pdf/common/Table';
import { useActivitiesFocusArea, useLabels } from '@/wpt-lib/pdf/useData';
import Heading from '@/wpt-lib/pdf/common/Heading';
import { View } from '@react-pdf/renderer';
import { ActivityFocusArea } from '@/wpt-lib/interfaces/Activity';
import { groupBy, orderBy } from 'lodash';
import NameKPI from '@/wpt-lib/pdf/activity/NameKPI';
import Leadership from '@/wpt-lib/pdf/activity/Leadership';
import Strategy2027 from '@/wpt-lib/pdf/activity/Strategy2027';
import ProgressUpdates from '@/wpt-lib/pdf/ProgressUpdates';
import Benchmark from '@/wpt-lib/pdf/activity/teaching/TeachingBenchmark';
import Collaborators from '@/wpt-lib/pdf/activity/Collaborators';
import SubjectDelivery from '@/wpt-lib/pdf/activity/teaching/SubjectDelivery';
import ShortCourseDelivery from '@/wpt-lib/pdf/activity/teaching/ShortCourseDelivery';
import MicroCredentialsDelivery from '@/wpt-lib/pdf/activity/teaching/MicroCredentialsDelivery';
import ContentCreation from '@/wpt-lib/pdf/activity/teaching/ContentCreation';
import CourseworkSupervision from '@/wpt-lib/pdf/activity/teaching/CourseworkSupervision';
import Other from '@/wpt-lib/pdf/activity/teaching/Other';
import ProfessionalDevelopment from '@/wpt-lib/pdf/activity/teaching/ProfessionalDevelopment';
import LearningExperienceDesign from '@/wpt-lib/pdf/activity/teaching/LearningExperienceDesign';
import Comments from '@/wpt-lib/pdf/Comments';
import AcademicCourseDelivery from '@/wpt-lib/pdf/activity/teaching/AcademicCourseDelivery';
import ExpectedOutcomes from '@/wpt-lib/pdf/activity/ExpectedOutcomes';
import InOrAboveLoad from '@/wpt-lib/pdf/activity/teaching/InOrAboveLoad';
import SubHeading from '@/wpt-lib/pdf/common/SubHeading';

const ActivityTitles: {
    [type: string]: string;
} = {
    delivery_subject: 'Subject Delivery',
    'delivery_short-course': 'Short Course Delivery',
    'delivery_micro-credential': 'Micro-credential Delivery',
    'delivery_academic-course': 'Academic Course Delivery',
    'content-creation': 'Content Creation',
    'learning-experience-design': 'Learning Experience Design',
    'coursework-project-supervision': 'Coursework Project & Other Project Supervision',
    other: 'Other Teaching Activity',
    'professional-development': 'Professional Development Teaching Activity',
    'teaching-pedagogy-goal': 'Teaching / Pedagogy Goal',
};

// eslint-disable-next-line
const ActivityComponents: Record<string, React.ComponentType> = {
    delivery_subject: SubjectDelivery,
    'delivery_short-course': ShortCourseDelivery,
    'delivery_micro-credential': MicroCredentialsDelivery,
    'delivery_academic-course': AcademicCourseDelivery,
    'content-creation': ContentCreation,
    'learning-experience-design': LearningExperienceDesign,
    'coursework-project-supervision': CourseworkSupervision,
    'professional-development': ProfessionalDevelopment,
    'teaching-pedagogy-goal': ExpectedOutcomes,
    other: Other,
};

const TeachingActivities = () => {
    const order = Object.keys(ActivityTitles);
    const teaching = orderBy(
        Object.entries(
            groupBy(
                useActivitiesFocusArea(ActivityFocusArea.TEACHING),
                (a) =>
                    `${a.data.teachingEducational}${
                        a.data.teachingEducational === 'delivery' ? '_' + a.data.teachingSubjectCourseType : ''
                    }`,
            ),
        ),
        (v) => order.indexOf(v[0]),
    );

    const titleLabels = useLabels('teachingEducational');

    return teaching.length > 0 ? (
        <Page>
            <Heading text="Teaching Activities" />

            {teaching.map(([type, typeActivities]) => (
                <View key={type}>
                    <Table cols={4}>
                        <Row>
                            <SubHeading level={1} text={titleLabels[type] ?? ActivityTitles[type]} />
                        </Row>
                        {typeActivities.map((activity, i) => (
                            <View key={i}>
                                <NameKPI {...activity.data} />
                                {type in ActivityComponents &&
                                    React.createElement(ActivityComponents[type], {
                                        ...activity.data,
                                    })}
                                <Collaborators {...activity.data} />
                                <Leadership {...activity.data} />
                                <InOrAboveLoad {...activity.data} />
                                <Benchmark {...activity.data} />
                                <Strategy2027 {...activity.data} />
                                <ProgressUpdates item={activity.workplanItem} />
                                <Comments comments={activity.workplanItem.comments} />
                                <Row>
                                    <Cell bg="#9faabd" />
                                </Row>
                            </View>
                        ))}
                    </Table>
                </View>
            ))}
        </Page>
    ) : null;
};

export default TeachingActivities;
